/**
 * Floating Navigation — Sticky side menu (desktop) / FAB drawer (mobile).
 * Quick access to the most important sections of the landing.
 */
import React, { useState, useEffect } from 'react';
import { Navigation2, X, PieChart, Target, TrendingUp, Flame, Trophy, Shield } from 'lucide-react';
import './FloatingNav.css';

const SECTIONS = [
  { id: 'calculadora', label: 'Test & Herramientas', icon: PieChart },
  { id: 'reto-18-meses', label: 'Reto 18 meses', icon: Trophy },
  { id: 'productos', label: 'Optimaxx Plus', icon: Shield },
  { id: 'email-capture', label: 'Asesoría sin costo', icon: TrendingUp, highlight: true }
];

const FloatingNav = () => {
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const [activeId, setActiveId] = useState(null);

  // Show after user scrolls past hero
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 600);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Track active section via IntersectionObserver
  useEffect(() => {
    const observed = SECTIONS
      .map((s) => document.getElementById(s.id))
      .filter(Boolean);
    if (observed.length === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const inView = entries.filter((e) => e.isIntersecting);
        if (inView.length > 0) {
          // Pick the entry closest to the top
          const sorted = [...inView].sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          setActiveId(sorted[0].target.id);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: 0 }
    );

    observed.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleNavClick = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setOpen(false);
  };

  if (!visible) return null;

  return (
    <>
      {/* Desktop: sticky side rail */}
      <nav className="floating-nav-desktop" aria-label="Navegación rápida" data-testid="floating-nav-desktop">
        {SECTIONS.map((s) => {
          const Icon = s.icon;
          const isActive = activeId === s.id;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => handleNavClick(s.id)}
              className={`fnav-item ${isActive ? 'fnav-active' : ''} ${s.highlight ? 'fnav-highlight' : ''}`}
              data-testid={`fnav-${s.id}`}
              aria-label={s.label}
            >
              <Icon className="fnav-icon" />
              <span className="fnav-label">{s.label}</span>
            </button>
          );
        })}
      </nav>

      {/* Mobile: floating button + drawer */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="floating-nav-fab"
        aria-label="Abrir menú rápido"
        data-testid="floating-nav-fab"
      >
        <Navigation2 className="w-5 h-5" />
      </button>

      {open && (
        <>
          <div
            className="floating-nav-backdrop"
            onClick={() => setOpen(false)}
            aria-hidden="true"
            data-testid="floating-nav-backdrop"
          />
          <div className="floating-nav-drawer" role="dialog" aria-label="Menú rápido" data-testid="floating-nav-drawer">
            <div className="fnav-drawer-header">
              <span className="fnav-drawer-title">¿A dónde vamos?</span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="fnav-drawer-close"
                aria-label="Cerrar menú"
                data-testid="floating-nav-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="fnav-drawer-list">
              {SECTIONS.map((s) => {
                const Icon = s.icon;
                const isActive = activeId === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => handleNavClick(s.id)}
                    className={`fnav-drawer-item ${isActive ? 'fnav-drawer-active' : ''} ${s.highlight ? 'fnav-drawer-highlight' : ''}`}
                    data-testid={`fnav-mobile-${s.id}`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{s.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default FloatingNav;
